import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, apiDownload } from '../api/client.ts';
import { Alert, ConfirmDialog, LoadingBlock, classes } from '../components/ui.tsx';
import { WorkPage, WorkSection, WorkEmpty, StaffNavigation } from '../components/work-page.tsx';
import { useSession } from '../state/session.tsx';

export const documentCopy = {
  en: {
    title: 'Private documents',
    back: 'Staff',
    privacy: 'Only you and owners or managers of this location can access your documents.',
    type: 'Document type',
    w9: 'W-9',
    id: 'ID',
    certification: 'Certification',
    other: 'Other',
    file: 'File or photo',
    limit: 'Choose a PDF, JPEG, PNG or WebP file, up to 10 MB.',
    upload: 'Upload document',
    retry: 'Retry upload',
    refresh: 'Refresh',
    more: 'Load more',
    empty: 'No documents yet.',
    download: 'Download',
    own: 'Your document',
    former: 'Former team member',
    saved: 'Document saved.',
    invalid: 'Choose a supported file of up to 10 MB.',
    offline: 'Connect to view or upload documents. Your selected file stays in memory while this page is open.',
    failed: 'The upload could not be confirmed. Retry the same file before making another change.',
    rejected: 'The upload was refused. Check the selected file and try again.',
    readFailed: 'Documents could not be loaded. Refresh to try again.',
    downloadFailed: 'The document could not be downloaded. Try again.',
    unavailable: 'Private documents are not available for this account and location.',
    team: 'Team checklist',
    teamHint: 'Choose a person to see, add or fix their documents.',
    person: 'Team member',
    allTeam: 'All team',
    yourDocuments: 'Your documents',
    recent: 'Recently added',
    certifications: 'Certifications',
    others: 'Other documents',
    onFile: 'on file',
    missing: 'missing',
    none: 'None yet.',
    expired: 'Expired',
    expiring: 'Expires soon',
    expires: 'Expires',
    added: 'Added',
    titleLabel: 'Title (optional)',
    titleHint: 'For example, OSHA 10',
    expiresLabel: 'Expiration date (optional)',
    you: 'you',
    view: 'View documents',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    remove: 'Remove',
    removeTitle: 'Remove this document?',
    removeBody: 'The file is deleted for everyone who can see it. This cannot be undone.',
    updated: 'Document updated.',
    removed: 'Document removed.',
    changeFailed: 'The change could not be saved. Try again.',
  },
  es: {
    title: 'Documentos privados',
    back: 'Personal',
    privacy: 'Solo usted y los propietarios o gerentes de esta ubicación pueden acceder a sus documentos.',
    type: 'Tipo de documento',
    w9: 'W-9',
    id: 'Identificación',
    certification: 'Certificación',
    other: 'Otro',
    file: 'Archivo o foto',
    limit: 'Elija un PDF, JPEG, PNG o WebP de hasta 10 MB.',
    upload: 'Subir documento',
    retry: 'Reintentar subida',
    refresh: 'Actualizar',
    more: 'Cargar más',
    empty: 'Todavía no hay documentos.',
    download: 'Descargar',
    own: 'Su documento',
    former: 'Exintegrante del equipo',
    saved: 'Documento guardado.',
    invalid: 'Elija un archivo compatible de hasta 10 MB.',
    offline:
      'Conéctese para consultar o subir documentos. El archivo seleccionado permanece en la memoria mientras esta página esté abierta.',
    failed: 'No se pudo confirmar la subida. Reintente con el mismo archivo antes de realizar otro cambio.',
    rejected: 'Se rechazó la subida. Revise el archivo seleccionado e inténtelo de nuevo.',
    readFailed: 'No se pudieron cargar los documentos. Actualice para reintentarlo.',
    downloadFailed: 'No se pudo descargar el documento. Inténtelo de nuevo.',
    unavailable: 'Los documentos privados no están disponibles para esta cuenta y ubicación.',
    team: 'Lista del equipo',
    teamHint: 'Elija a una persona para ver, agregar o corregir sus documentos.',
    person: 'Integrante del equipo',
    allTeam: 'Todo el equipo',
    yourDocuments: 'Sus documentos',
    recent: 'Agregados recientemente',
    certifications: 'Certificaciones',
    others: 'Otros documentos',
    onFile: 'en archivo',
    missing: 'falta',
    none: 'Ninguno todavía.',
    expired: 'Vencido',
    expiring: 'Vence pronto',
    expires: 'Vence',
    added: 'Agregado',
    titleLabel: 'Título (opcional)',
    titleHint: 'Por ejemplo, OSHA 10',
    expiresLabel: 'Fecha de vencimiento (opcional)',
    you: 'usted',
    view: 'Ver documentos',
    edit: 'Editar',
    save: 'Guardar',
    cancel: 'Cancelar',
    remove: 'Eliminar',
    removeTitle: '¿Eliminar este documento?',
    removeBody: 'El archivo se elimina para todas las personas que pueden verlo. No se puede deshacer.',
    updated: 'Documento actualizado.',
    removed: 'Documento eliminado.',
    changeFailed: 'No se pudo guardar el cambio. Inténtelo de nuevo.',
  },
} as const;
type Kind = 'w9' | 'id' | 'certification' | 'other';
const kinds = ['w9', 'id', 'certification', 'other'] as const;
type Document = {
  id: string;
  documentType: Kind;
  title?: string | null;
  originalFilename: string;
  sizeBytes: number;
  createdAt?: string;
  expiresOn?: string | null;
  personId?: string;
  downloadUrl: string;
  own: boolean;
  canChange?: boolean;
  name: string | null;
};
type List = { items: Document[]; nextCursor: string | null; canWrite: boolean; canManage: boolean };
type Person = {
  id: string;
  name: string | null;
  documents: Record<Kind, number>;
  expired: number;
  expiring: number;
};
type Edit = { id: string; personId: string; type: Kind; title: string; expiresOn: string };
type Copy = (typeof documentCopy)[keyof typeof documentCopy];
/** The reader's own calendar date, which expiry is judged against. */
const today = () => new Date().toLocaleDateString('en-CA');
function expiry(date: string | null | undefined) {
  if (!date) return null;
  const now = today(),
    soon = new Date(Date.parse(`${now}T00:00:00Z`) + 30 * 86_400_000).toISOString().slice(0, 10);
  return date < now ? 'expired' : date <= soon ? 'expiring' : 'current';
}
const ok = 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
  warn = 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
  bad = 'border-[color:var(--color-danger)] text-[color:var(--color-danger)]';
/** W-9 and ID on file, certifications counted, and anything lapsed or lapsing. */
function Checklist({ copy, person }: { copy: Copy; person: Omit<Person, 'id' | 'name'> }) {
  return (
    <div className="flex flex-wrap gap-2">
      {(['w9', 'id'] as const).map((kind) => (
        <span key={kind} className={classes('badge', person.documents[kind] ? ok : warn)}>
          {copy[kind]}: {person.documents[kind] ? copy.onFile : copy.missing}
        </span>
      ))}
      <span className={classes('badge', person.documents.certification ? ok : 'border-[color:var(--color-line)]')}>
        {copy.certifications}: {person.documents.certification}
      </span>
      {person.expired > 0 && (
        <span className={classes('badge', bad)}>
          {copy.expired}: {person.expired}
        </span>
      )}
      {person.expiring > 0 && (
        <span className={classes('badge', warn)}>
          {copy.expiring}: {person.expiring}
        </span>
      )}
    </div>
  );
}
export default function StaffDocumentsPage() {
  const s = useSession();
  const scope = JSON.stringify([
    s.user?.companyId,
    s.user?.id,
    s.currentLocation?.id,
    s.user?.role,
    [...(s.user?.permissions ?? [])].sort(),
    [...(s.company?.planFeatures ?? [])].sort(),
    s.company?.accessLocked,
    s.impersonatedBy,
  ]);
  return <DocumentsContent key={scope} />;
}
function DocumentsContent() {
  const { i18n } = useTranslation(),
    copy = documentCopy[i18n.language.startsWith('es') ? 'es' : 'en'];
  const self = useSession().user?.id ?? '';
  const [list, setList] = useState<List | null>(null),
    [people, setPeople] = useState<Person[] | null>(null),
    [person, setPerson] = useState<string | null>(() => new URLSearchParams(window.location.search).get('person')),
    [file, setFile] = useState<File | null>(null),
    [type, setType] = useState<Kind>('w9'),
    [owner, setOwner] = useState(''),
    [title, setTitle] = useState(''),
    [expiresOn, setExpiresOn] = useState(''),
    [editing, setEditing] = useState<Edit | null>(null),
    [removing, setRemoving] = useState<Document | null>(null);
  const [error, setError] = useState(''),
    [message, setMessage] = useState(''),
    [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false);
  const alive = useRef(true),
    locked = useRef(false),
    generation = useRef(0),
    input = useRef<HTMLInputElement>(null);
  const pending = useRef<{
    id: string;
    file: File;
    type: Kind;
    personId: string;
    title: string;
    expiresOn: string;
  } | null>(null);
  useEffect(() => {
    alive.current = true;
    void refresh(false, person);
    return () => {
      alive.current = false;
      generation.current++;
      pending.current = null;
    };
  }, []);
  function clearPrivate() {
    pending.current = null;
    setList(null);
    setPeople(null);
    setEditing(null);
    setRemoving(null);
    setFile(null);
    setType('w9');
    setTitle('');
    setExpiresOn('');
    setUncertain(false);
    setMessage('');
    if (input.current) input.current.value = '';
  }
  async function refresh(more = false, who = person) {
    if (!navigator.onLine) {
      setError(copy.offline);
      return;
    }
    const gen = ++generation.current;
    try {
      const query = new URLSearchParams();
      if (who) query.set('person', who);
      if (more && list?.nextCursor) query.set('before', list.nextCursor);
      const next = await api.getFresh<List>(`/staff/documents${query.toString() ? `?${query}` : ''}`);
      // The checklist counts every document, not just the page loaded below.
      const roster = await api.getFresh<{ people: Person[] }>(`/staff/documents/people?today=${today()}`);
      if (!alive.current || gen !== generation.current) return;
      setList(more && list ? { ...next, items: [...list.items, ...next.items] } : next);
      setPeople(roster.people);
      setError('');
    } catch (e) {
      if (alive.current && gen === generation.current) {
        setList(null);
        if ([401, 402, 403, 404].includes((e as { status?: number }).status ?? 0)) clearPrivate();
        setError(
          [401, 402, 403, 404].includes((e as { status?: number }).status ?? 0) ? copy.unavailable : copy.readFailed,
        );
      }
    }
  }
  async function upload() {
    if (locked.current) return;
    if (!navigator.onLine) {
      setError(copy.offline);
      return;
    }
    if (!pending.current) {
      if (
        !file ||
        file.size === 0 ||
        file.size > 10 * 1024 * 1024 ||
        !['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(file.type)
      ) {
        setError(copy.invalid);
        return;
      }
      pending.current = {
        id: crypto.randomUUID(),
        file,
        type,
        personId: list?.canManage ? owner || uploadFor() : '',
        title,
        expiresOn,
      };
    }
    locked.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    const draft = pending.current,
      form = new FormData();
    form.set('file', draft.file);
    form.set('documentType', draft.type);
    form.set('clientUploadId', draft.id);
    if (draft.personId) form.set('personId', draft.personId);
    if (draft.title.trim()) form.set('title', draft.title.trim());
    if (draft.expiresOn) form.set('expiresOn', draft.expiresOn);
    try {
      await api.upload('/staff/documents', form);
      if (!alive.current) return;
      pending.current = null;
      setUncertain(false);
      setFile(null);
      setTitle('');
      setExpiresOn('');
      if (input.current) input.current.value = '';
      setMessage(copy.saved);
      await refresh();
    } catch (e) {
      if (alive.current) {
        const status = (e as { status?: number }).status ?? 0;
        if ([401, 402, 403, 404].includes(status)) {
          clearPrivate();
          setError(copy.unavailable);
        } else if (status >= 400 && status < 500 && ![408, 429].includes(status)) {
          pending.current = null;
          setUncertain(false);
          setError(copy.rejected);
        } else {
          setUncertain(true);
          setError(copy.failed);
        }
      }
    } finally {
      locked.current = false;
      if (alive.current) setBusy(false);
    }
  }
  /** Refile (edit) or remove one document, then reread the list and checklist. */
  async function change(id: string, body: Omit<Edit, 'id'> | null) {
    if (locked.current) return;
    if (!navigator.onLine) {
      setError(copy.offline);
      return;
    }
    locked.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (body)
        await api.patch(`/staff/documents/${id}`, {
          ...(list?.canManage ? { personId: body.personId } : {}),
          documentType: body.type,
          title: body.title.trim() || null,
          expiresOn: body.expiresOn || null,
        });
      else await api.delete(`/staff/documents/${id}`);
      if (!alive.current) return;
      setEditing(null);
      setRemoving(null);
      setMessage(body ? copy.updated : copy.removed);
      await refresh();
    } catch (e) {
      if (alive.current) {
        if ([401, 402].includes((e as { status?: number }).status ?? 0)) {
          clearPrivate();
          setError(copy.unavailable);
        } else setError(copy.changeFailed);
      }
    } finally {
      locked.current = false;
      if (alive.current) setBusy(false);
    }
  }
  /** The person a manager's upload is filed for: the open folder, unless they have left. */
  function uploadFor() {
    return person && people?.some((member) => member.id === person && member.name !== null) ? person : '';
  }
  function choose(id: string | null) {
    setPerson(id);
    setOwner('');
    setEditing(null);
    setList(null);
    void refresh(false, id);
  }
  async function download(row: Document) {
    if (locked.current) return;
    if (!navigator.onLine) {
      setError(copy.offline);
      return;
    }
    locked.current = true;
    setBusy(true);
    setError('');
    try {
      const data = await apiDownload(row.downloadUrl);
      if (!alive.current) return;
      const url = URL.createObjectURL(data.blob),
        link = document.createElement('a');
      link.href = url;
      link.download = data.filename ?? row.originalFilename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (alive.current) {
        if ([401, 402, 403].includes((e as { status?: number }).status ?? 0)) clearPrivate();
        setError(copy.downloadFailed);
      }
    } finally {
      locked.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const disabled = busy || uncertain,
    button = 'btn btn-secondary';
  const manager = Boolean(list?.canManage),
    // A manager without a person chosen sees the whole team; anyone else, one folder.
    folder = !list ? null : manager ? person : self,
    current = (people ?? []).filter((member) => member.name !== null),
    chosen = people?.find((member) => member.id === person),
    summary = people?.find((member) => member.id === folder);
  const personOptions = (selected?: string) => (
    <>
      {current.map((member) => (
        <option value={member.id} key={member.id}>
          {member.id === self ? `${member.name} (${copy.you})` : member.name}
        </option>
      ))}
      {selected && !current.some((member) => member.id === selected) && <option value={selected}>{copy.former}</option>}
    </>
  );
  function row(item: Document, showPerson: boolean) {
    const status = expiry(item.expiresOn),
      edit = editing?.id === item.id ? editing : null;
    return (
      <article key={item.id} className="work-row mb-3 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="break-all font-semibold">{item.title || item.originalFilename}</p>
            <p>
              {[
                // Inside a person's folder the group heading already names the kind.
                showPerson ? copy[item.documentType] : null,
                showPerson ? (item.own ? copy.own : (item.name ?? copy.former)) : null,
                item.title ? item.originalFilename : null,
                `${Math.ceil(item.sizeBytes / 1024)} KB`,
                item.createdAt ? `${copy.added} ${new Date(item.createdAt).toLocaleDateString(i18n.language)}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {status && (
              <span className={classes('badge mt-1', status === 'expired' ? bad : status === 'expiring' ? warn : ok)}>
                {status === 'current' ? copy.expires : copy[status]} {item.expiresOn}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <button className={button} disabled={disabled} onClick={() => void download(item)}>
              {copy.download}
            </button>
            {item.canChange && !edit && (
              <>
                <button
                  className={button}
                  disabled={disabled}
                  onClick={() =>
                    setEditing({
                      id: item.id,
                      personId: item.personId ?? '',
                      type: item.documentType,
                      title: item.title ?? '',
                      expiresOn: item.expiresOn ?? '',
                    })
                  }
                >
                  {copy.edit}
                </button>
                <button className={button} disabled={disabled} onClick={() => setRemoving(item)}>
                  {copy.remove}
                </button>
              </>
            )}
          </div>
        </div>
        {edit && (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              const { id, ...body } = edit;
              void change(id, body);
            }}
          >
            {manager && (
              <label className="field-label">
                {copy.person}
                <select
                  className="field-control mt-1"
                  value={edit.personId}
                  disabled={busy}
                  onChange={(e) => setEditing({ ...edit, personId: e.target.value })}
                >
                  {personOptions(edit.personId)}
                </select>
              </label>
            )}
            <label className="field-label">
              {copy.type}
              <select
                className="field-control mt-1"
                value={edit.type}
                disabled={busy}
                onChange={(e) => setEditing({ ...edit, type: e.target.value as Kind })}
              >
                {kinds.map((kind) => (
                  <option value={kind} key={kind}>
                    {copy[kind]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-label">
              {copy.titleLabel}
              <input
                className="field-control mt-1"
                maxLength={120}
                value={edit.title}
                disabled={busy}
                onChange={(e) => setEditing({ ...edit, title: e.target.value })}
              />
            </label>
            <label className="field-label">
              {copy.expiresLabel}
              <input
                className="field-control mt-1"
                type="date"
                value={edit.expiresOn}
                disabled={busy}
                onChange={(e) => setEditing({ ...edit, expiresOn: e.target.value })}
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button className="btn btn-primary" disabled={busy}>
                {copy.save}
              </button>
              <button type="button" className={button} disabled={busy} onClick={() => setEditing(null)}>
                {copy.cancel}
              </button>
            </div>
          </form>
        )}
      </article>
    );
  }
  return (
    <WorkPage
      title={copy.title}
      description={copy.privacy}
      actions={
        <button className={button} disabled={busy} onClick={() => void refresh()}>
          {copy.refresh}
        </button>
      }
    >
      <StaffNavigation active="documents" canManage={list?.canManage} ownDocuments={!list?.canManage} />
      {error && <Alert tone="error">{error}</Alert>}
      {message && <Alert tone="success">{message}</Alert>}
      {!list && !error && <LoadingBlock />}
      {manager && !person && people && (
        <WorkSection title={copy.team} description={copy.teamHint}>
          {people.map((member) => (
            <article key={member.id} className="work-row mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 space-y-2">
                <p className="font-semibold">{member.name ?? copy.former}</p>
                <Checklist copy={copy} person={member} />
              </div>
              <button className={button} disabled={busy} onClick={() => choose(member.id)}>
                {copy.view}
              </button>
            </article>
          ))}
        </WorkSection>
      )}
      {folder && (
        <WorkSection
          title={manager ? (person === self ? copy.yourDocuments : (chosen?.name ?? copy.former)) : copy.yourDocuments}
          actions={
            manager ? (
              <button className={button} disabled={busy} onClick={() => choose(null)}>
                {copy.allTeam}
              </button>
            ) : undefined
          }
        >
          {summary && <Checklist copy={copy} person={summary} />}
          {list && !list.items.length && (
            <WorkEmpty title={copy.empty}>
              <p>{copy.privacy}</p>
            </WorkEmpty>
          )}
          {list &&
            list.items.length > 0 &&
            kinds.map((kind) => {
              const group = list.items.filter((item) => item.documentType === kind);
              return (
                <div key={kind} className="mt-4">
                  <h3 className="mb-2 font-semibold">
                    {kind === 'certification' ? copy.certifications : kind === 'other' ? copy.others : copy[kind]}
                  </h3>
                  {group.length ? group.map((item) => row(item, false)) : <p>{copy.none}</p>}
                </div>
              );
            })}
          {list?.nextCursor && (
            <button className={button} disabled={busy} onClick={() => void refresh(true)}>
              {copy.more}
            </button>
          )}
        </WorkSection>
      )}
      {list?.canWrite && (
        <form
          className="card space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void upload();
          }}
        >
          <h2 className="text-lg font-semibold">{copy.upload}</h2>
          {manager && current.length > 0 && (
            <label className="field-label">
              {copy.person}
              <select
                className="field-control mt-1"
                value={owner || uploadFor() || self}
                disabled={disabled}
                onChange={(e) => setOwner(e.target.value)}
              >
                {personOptions()}
              </select>
            </label>
          )}
          <label className="field-label">
            {copy.type}
            <select
              className="field-control mt-1"
              value={type}
              disabled={disabled}
              onChange={(e) => setType(e.target.value as Kind)}
            >
              {kinds.map((kind) => (
                <option value={kind} key={kind}>
                  {copy[kind]}
                </option>
              ))}
            </select>
          </label>
          <label className="field-label">
            {copy.titleLabel}
            <input
              className="field-control mt-1"
              maxLength={120}
              placeholder={copy.titleHint}
              value={title}
              disabled={disabled}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="field-label">
            {copy.expiresLabel}
            <input
              className="field-control mt-1"
              type="date"
              value={expiresOn}
              disabled={disabled}
              onChange={(e) => setExpiresOn(e.target.value)}
            />
          </label>
          <label className="block">
            {copy.file}
            <input
              ref={input}
              className="field-control mt-1 max-w-full"
              type="file"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              disabled={disabled}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          <p>{copy.limit}</p>
          <button className={button} disabled={disabled || !file}>
            {copy.upload}
          </button>
        </form>
      )}
      {uncertain && (
        <button className={button} disabled={busy} onClick={() => void upload()}>
          {copy.retry}
        </button>
      )}
      {list && !folder && (
        <WorkSection title={copy.recent}>
          {!list.items.length && (
            <WorkEmpty title={copy.empty}>
              <p>{copy.privacy}</p>
            </WorkEmpty>
          )}
          {list.items.map((item) => row(item, true))}
          {list.nextCursor && (
            <button className={button} disabled={busy} onClick={() => void refresh(true)}>
              {copy.more}
            </button>
          )}
        </WorkSection>
      )}
      {removing && (
        <ConfirmDialog
          title={copy.removeTitle}
          confirmLabel={copy.remove}
          cancelLabel={copy.cancel}
          confirmVariant="danger"
          busy={busy}
          onConfirm={() => void change(removing.id, null)}
          onCancel={() => setRemoving(null)}
        >
          <p className="break-all font-semibold">{removing.title || removing.originalFilename}</p>
          <p>{copy.removeBody}</p>
        </ConfirmDialog>
      )}
    </WorkPage>
  );
}
